import type { GymEquipmentModel } from "./gyms.model";

/**
 * ⚠️ THE HEADINGS THE DESIGN GROUPS KIT UNDER LIVE HERE, IN THE SCREEN, AND THAT IS A KNOWN
 * COMPROMISE — NOT AN OVERSIGHT.
 *
 * The design (handoff Screen 7) groups equipment into rows — Machines, Rig, and so on. The
 * service has no idea which heading a piece of kit belongs under: `gym_equipment` carries an
 * `item` and nothing else, and the engine's own `EquipmentCategory` lives inside
 * `api/src/engine/vocabulary/` where nothing in the app may reach. So the mapping is a static
 * table in the one screen that needs it.
 *
 * WHAT THIS COSTS: a piece of kit added to the engine's registry and not added below falls
 * into `odds`, which is a quiet wrong answer rather than a loud one. Nothing fails.
 *
 * 🚨 BUD-304 records the gap and asks the actual question — whether the grouping belongs in
 * the schema (a category column, or a shipped registry both sides read) instead of here.
 * DO NOT add a column to answer it locally; that is a migration this change may not run.
 */

/** The order rows appear in, top to bottom. The design's order, with the racks first. */
export const EQUIPMENT_GROUPS = [
  "plate",
  "kettlebell",
  "dumbbell",
  "medBall",
  "bar",
  "machines",
  "rig",
  "odds",
] as const;

export type EquipmentGroupId = (typeof EQUIPMENT_GROUPS)[number];

/**
 * HOW A GROUP'S WEIGHTS ARE WORDED, and why there are three answers rather than one.
 *
 *   ceiling — a plate rack is described by its biggest plate. "to 25 kg" is what an athlete
 *             says about a rack of plates; nobody lists 1.25 · 2.5 · 5 · 10 · 15 · 20 · 25.
 *   list    — few enough sizes to name. "8 · 12 · 16 kg", and the gap left by the 20 kg bell
 *             somebody walked off with is VISIBLE, which a range would hide.
 *   range   — too many to name. "5–30 kg".
 *
 * `list` and `range` are one rule with a threshold, not two decisions: see `LIST_CEILING`.
 */
type WeightStyle = "ceiling" | "sized" | "none";

/**
 * ⚠️ THE ITEM IDS BELOW ARE THE ENGINE'S, copied rather than imported — `api/**` is not
 * reachable from `src/**` and must not become so. They were read from
 * `api/src/engine/vocabulary/equipment.ts`; `plate` is the one id that is NOT in that
 * registry (a plate is a rack the engine reads, not a thing a movement can require) and it
 * is named in `shared/contracts/gyms.ts` instead.
 */
const GROUP_OF_ITEM: Readonly<Record<string, EquipmentGroupId>> = {
  plate: "plate",
  kettlebell: "kettlebell",
  dumbbell: "dumbbell",
  "med-ball": "medBall",

  barbell: "bar",
  rack: "bar",
  bench: "bar",

  rower: "machines",
  bike: "machines",
  "ski-erg": "machines",

  rig: "rig",
  rings: "rig",
  box: "rig",
  rope: "rig",

  "jump-rope": "odds",
  band: "odds",
  "foam-roller": "odds",
  pvc: "odds",
};

/**
 * THE STANDARD KIT A GYM STOCKS, AND EVERY SIZE OF IT — the set a NEW gym starts with, all
 * of it switched ON (BUD-310).
 *
 * "The gym has it" is the common case, so subtracting what is missing is less work than
 * adding everything from scratch. Every size below is a pill the athlete switches off, never
 * a number they type — which is the whole point of the issue: the previous editor asked for a
 * weight and a count per size and the owner's verdict on it was "a total no go".
 *
 * The values are v1's own (`~/Development/buddy/src/lib/buddy/boxes.ts`), which is the model
 * being ported: the standard bumper/metal plate set, competition kettlebells 8–32 kg in 4 kg
 * steps, and a commercial fixed dumbbell rack in 2.5 kg steps to 30 kg. Med balls have no v1
 * precedent — the three wall-ball weights a box actually owns.
 */
export const CATALOG_SIZES_KG: Readonly<Record<string, readonly number[]>> = {
  plate: [1.25, 2.5, 5, 10, 15, 20, 25],
  kettlebell: [8, 12, 16, 20, 24, 28, 32],
  dumbbell: [2.5, 5, 7.5, 10, 12.5, 15, 17.5, 20, 22.5, 25, 27.5, 30],
  "med-ball": [4, 6, 9],
};

/**
 * THE THINGS A STOCKED GYM DOES *NOT* START WITH.
 *
 * "Everything on" is the right default only for kit a typical gym actually stocks. These are
 * the ones the owner switched off on every gym they entered, which is the same evidence the
 * rest of the catalog is on by default for — a default that is wrong every time costs a tap
 * every time, in the direction this editor exists to remove.
 *
 *   ski-erg  — the third erg, and the one most rooms do not own.
 *   rope     — a climbing rope needs a ceiling most commercial gyms do not have.
 *   1.25 kg plate — the smallest change plate; a rack usually starts at 2.5.
 *   4 kg med ball — below the lightest wall ball in normal use.
 *   28 and 32 kg kettlebell — the top two of the competition set. A commercial rack
 *   commonly stops at 24; the two above it are a strength-gym purchase, and switching them
 *   off was the correction the owner made on every room they entered.
 *
 * Anything here is still one tap away: the pill is present and switched off, not missing.
 */
export const OFF_BY_DEFAULT_ITEMS: readonly string[] = ["ski-erg", "rope"];

/** The same rule for sizes — a pill that is offered but starts off. */
export const OFF_BY_DEFAULT_SIZES: Readonly<Record<string, readonly number[]>> = {
  plate: [1.25],
  kettlebell: [28, 32],
  "med-ball": [4],
};

/**
 * THE BAR'S OWN WEIGHT, ASSUMED RATHER THAN ASKED (BUD-310).
 *
 * `roomOf` in `api/src/domains/sessions/logic.ts` reads a barbell row's `weightKg` as
 * `Room.barKg` and rounds every prescribed load against it; zero means "no bar weight we know
 * of", which prints loads nobody can build. A standard men's Olympic bar is 20 kg and that is
 * what stands in the room the athlete is toggling — so the barbell pill carries it, and the
 * athlete is never asked for a number. An athlete with a 15 kg bar is the case this trades
 * away, deliberately: it is a rounding difference, and the alternative is a text field on the
 * one screen the owner asked to have no text fields on.
 */
export const ASSUMED_BAR_KG = 20;

const WEIGHT_STYLE: Readonly<Record<EquipmentGroupId, WeightStyle>> = {
  plate: "ceiling",
  kettlebell: "sized",
  dumbbell: "sized",
  medBall: "sized",
  bar: "none",
  machines: "none",
  rig: "none",
  odds: "none",
};

/** Above this many distinct sizes a list stops reading as a list and becomes a range. */
const LIST_CEILING = 3;

/**
 * Anything the table does not name. It is a real row rather than a silent drop: kit the
 * athlete entered has to appear somewhere, or the screen lies about the room.
 */
const FALLBACK_GROUP: EquipmentGroupId = "odds";

export interface EquipmentRow {
  /** The heading. A translation key under `rows.` in this feature's locale files. */
  group: EquipmentGroupId;
  /** Distinct sizes present, ascending. Empty for a group nothing sizes. */
  weights: number[];
  /** Equipment ids present with no size of their own, in the table's order above. */
  items: string[];
  /** How the weights should be worded — see `WeightStyle`. */
  weightStyle: WeightStyle;
}

/**
 * THE ROOM AS THE DESIGN'S QUIET ROWS.
 *
 * `present` kit becomes grouped rows; `absent` kit becomes ONE row at the bottom. That last
 * row is the design's replacement for red "missing" chips — what is not there is stated
 * plainly, once, and it is a fact the athlete entered rather than a warning the app invented.
 */
export interface RoomRows {
  rows: EquipmentRow[];
  /**
   * The "Not here" row — DERIVED, never stored (BUD-310). It is the catalog minus what is
   * present, so it cannot contradict the rows above it the way a stored third state can: a
   * gym could record "no kettlebells" and list kettlebell sizes at the same time (BUD-304),
   * and this shape has no way to express that. Empty means no row.
   */
  absent: string[];
}

const groupOf = (item: string): EquipmentGroupId => GROUP_OF_ITEM[item] ?? FALLBACK_GROUP;

export function buildRoomRows(equipment: readonly GymEquipmentModel[]): RoomRows {
  const weights = new Map<EquipmentGroupId, Set<number>>();
  const items = new Map<EquipmentGroupId, string[]>();
  const present = new Set<string>();

  for (const row of equipment) {
    /* A STORED ABSENCE IS READ AS "NOT PRESENT" AND NOTHING MORE. The editor stopped writing
       them (BUD-310) but rooms recorded before it still carry them, and they land in the same
       derived "Not here" row as anything else the gym does not have. */
    if (row.state === "absent") continue;

    present.add(row.item);
    const group = groupOf(row.item);

    /* SIZES ARE A RACK'S DESCRIPTION; A NAME IS EVERYTHING ELSE'S. A barbell carries the
       bar's own weight (see `ASSUMED_BAR_KG`) and is still read out as "Barbell" — a group
       nothing sizes has no wording for a weight, so printing one would say "Bar · 20 kg" and
       lose the word for the thing itself. */
    if (row.weightKg !== null && WEIGHT_STYLE[group] !== "none") {
      const set = weights.get(group) ?? new Set<number>();
      set.add(row.weightKg);
      weights.set(group, set);
      continue;
    }

    const named = items.get(group) ?? [];
    // One row per size means the same item arrives many times; the NAME appears once.
    if (!named.includes(row.item)) named.push(row.item);
    items.set(group, named);
  }

  const absent = CATALOG_ITEMS.filter((item) => !present.has(item));

  const rows = EQUIPMENT_GROUPS.map((group) => ({
    group,
    weights: [...(weights.get(group) ?? [])].sort((a, b) => a - b),
    items: items.get(group) ?? [],
    weightStyle: WEIGHT_STYLE[group],
  })).filter((row) => row.weights.length > 0 || row.items.length > 0);

  return { rows, absent };
}

/**
 * The weights of one row, as the design words them. Returns the pieces rather than a
 * sentence, because the unit and the "to" belong to a language and this file has none.
 */
export type WeightPhrase =
  | { kind: "ceiling"; max: number }
  | { kind: "list"; weights: number[] }
  | { kind: "range"; min: number; max: number };

export function weightPhrase(row: EquipmentRow): WeightPhrase | null {
  if (row.weights.length === 0) return null;

  const min = row.weights[0]!;
  const max = row.weights[row.weights.length - 1]!;

  if (row.weightStyle === "ceiling") return { kind: "ceiling", max };
  if (row.weights.length > LIST_CEILING) return { kind: "range", min, max };
  return { kind: "list", weights: row.weights };
}

/**
 * EVERY ITEM THE EDITOR CAN OFFER, grouped the same way the read view groups them, so the
 * two cannot drift: one table, read from both directions.
 */
export function itemsInGroup(group: EquipmentGroupId): string[] {
  return Object.keys(GROUP_OF_ITEM).filter((item) => GROUP_OF_ITEM[item] === group);
}

/** The groups whose kit comes in sizes — the ones the editor offers a pill per SIZE for. */
export function isSizedGroup(group: EquipmentGroupId): boolean {
  return WEIGHT_STYLE[group] !== "none";
}

/**
 * EVERY ITEM THE CATALOG NAMES, in the read view's own order. One list, read from both ends:
 * the editor offers a pill for each, and the "Not here" row is this minus what is present.
 */
export const CATALOG_ITEMS: readonly string[] = EQUIPMENT_GROUPS.flatMap(itemsInGroup);

/** Every item that is a yes or a no — one pill each. */
export const NAMED_ITEMS: readonly string[] = EQUIPMENT_GROUPS.filter(
  (group) => !isSizedGroup(group),
).flatMap(itemsInGroup);

/** Each sized group names exactly one item — plates, bells, dumbbells, med balls. */
export const SIZED_ITEMS: readonly string[] = EQUIPMENT_GROUPS.filter(isSizedGroup).map(
  (group) => itemsInGroup(group)[0]!,
);
